import React, { useMemo } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';

import { AVATAR_SHAPE_IDS, AVATAR_SHAPE_PATHS, type AvatarShapeId } from '../avatar/shapeCatalog';
import { useLocale } from '../i18n/LocaleContext';
import { useVisualTheme } from '../theme/VisualThemeContext';
import { chatListPalette } from '../theme/chatListPalette';
import { hapticSelection } from '../util/haptics';

/**
 * Горизонтальная полоса выбора формы аватара.
 *
 * <p>Была написана дважды — в `AvatarEditorPanel` и в её же дочернем `AvatarPhotoEditor`, —
 * сорок строк слово в слово. Полоса показывается в обоих состояниях панели (пока снимок
 * выбирают и пока его кадрируют), и это единственная причина, по которой копий было две;
 * форму же она задаёт одну и ту же, и разъехавшиеся копии означали бы, что до и после
 * кадрирования человеку предлагают разный набор.
 *
 * <p>Ничего не рисует, когда менять форму нельзя: у чужого аватара выбор формы — не серая
 * кнопка, а отсутствие раздела.
 */
export function AvatarShapeStrip({
  shapeId,
  onShapeChange,
}: {
  shapeId: AvatarShapeId;
  onShapeChange?: (id: AvatarShapeId) => void;
}) {
  const { t } = useLocale();
  const { resolvedVisualTheme } = useVisualTheme();
  const pal = useMemo(() => chatListPalette(resolvedVisualTheme), [resolvedVisualTheme]);

  if (!onShapeChange) return null;

  return (
    <View>
      <Text style={[styles.label, { color: pal.settingsFieldLabel }]}>
        {t('settingsAvatarChooseShape')}
      </Text>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.shapeStrip}>
        {AVATAR_SHAPE_IDS.map((id) => {
          const active = id === shapeId;
          return (
            <Pressable
              key={id}
              onPress={() => {
                if (active) return;
                hapticSelection();
                onShapeChange(id);
              }}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              accessibilityLabel={id}
              style={[
                styles.shapeCell,
                active && {
                  borderColor: pal.chatFilterChipActiveBorder,
                  backgroundColor: pal.chatFilterChipActive,
                },
              ]}>
              <Svg width={38} height={38} viewBox="0 0 100 100">
                <Path
                  d={AVATAR_SHAPE_PATHS[id]}
                  fill="transparent"
                  stroke={active ? pal.chatFilterChipTextActive : pal.settingsSectionTitle}
                  strokeWidth={2}
                />
              </Svg>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  label: { fontSize: 13, fontWeight: '600', marginTop: 2 },
  shapeStrip: { gap: 8, paddingVertical: 2 },
  shapeCell: { padding: 4, borderRadius: 10, borderWidth: 1, borderColor: 'transparent' },
});
